import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Loader2, X, Edit, Trash2 } from 'lucide-react';
import { userService } from '../../_services';

function WhManageGroup({
    text,
    whName,
    whId,
    whPrefix,
    whProfilePagin,
    whSearch,
    whEmail,
    closemanageGroupSection,
    openAddOverridePopup
}) {
    const [loading, setLoading] = useState(true);
    const [btnLoader, setBtnLoader] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [userList, setUserList] = useState([]);
    const [userListTemp, setUserListTemp] = useState([]);
    const [chainNamesList, setChainNamesList] = useState([]);
    const [userChainNamesList, setUserChainNamesList] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredList, setFilteredList] = useState([]);
    const [openPopovers, setOpenPopovers] = useState({});
    const [noInitialChainList, setNoInitialChainList] = useState(true);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                const data = text && JSON.parse(text);
                return data;
            } catch (error) {
                return [];
            }
        });
    }, []);

    const fetchDataLocally = useCallback(async (roleName, roleType, id) => {
        setLoading(true);
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roleName, roleType, id })
            };
            const response = await fetch(`/api/admin/GetGroupList`, requestOptions);
            const apidata = await handleResponse(response);
            
            if (apidata) {
                setUserList(apidata);
                setUserListTemp(apidata);
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    }, [handleResponse]);

    const fetchPharmacyChainList = useCallback(async () => {
        try {
            const response = await userService.fetchPharmacyChainList('a', 'a');
            const apidata = await handleResponse(response);
            if (apidata?.length > 0) {
                setChainNamesList(apidata);
            }
        } catch (error) {
        }
    }, [handleResponse]);

    const fetchUserPharmacyChainList = useCallback(async (Wholesaler_Name, Wholesaler_Id) => {
        try {
            const response = await userService.fetchUserPharmacyChainList(Wholesaler_Name, Wholesaler_Id, 0);
            const apidata = await handleResponse(response);
            
            if (apidata?.length > 0 && apidata[0].chainNames && apidata[0].chainNames.length > 0) {
                const chainData = apidata[0].chainNames.map(item => ({
                    chainName: item.chainName,
                    comm: item.comm,
                    editable: false
                }));
                setUserChainNamesList(chainData);
                
                // Set noInitialChainList flag based on whether data exists and is group specific
                const hasChains = apidata[0].chainNames && apidata[0].chainNames.length > 0;
                const isGroupSpecific = apidata[0].isGroupSpecific || false;
                setNoInitialChainList(!(hasChains && isGroupSpecific));
                
                // Set selected groups if available
                if (apidata[0].groupNumbers?.length > 0) {
                    const groupNumbers = apidata[0].groupNumbers.map(gn => gn.groupNumber);
                    setSelectedGroups(groupNumbers);
                }
            } else if (apidata?.length > 0) {
                // If no chain names, set default General with wholesaler commission or 0
                setUserChainNamesList([{
                    chainName: 'General',
                    comm: apidata[0].whCommission || '0',
                    editable: false
                }]);
                setNoInitialChainList(true);
            }
        } catch (error) {
        }
    }, [handleResponse]);

    useEffect(() => {
        if (userdata && whId) {
            fetchDataLocally('Admin', 'Admin', whId);
            fetchPharmacyChainList();
            fetchUserPharmacyChainList(whName, whId);
        }
    }, [userdata, whId, whName, fetchDataLocally, fetchPharmacyChainList, fetchUserPharmacyChainList]);

    const handleSearchChange = useCallback((e) => {
        const query = e.target.value;
        setSearchQuery(query);
        
        if (query.length > 0) {
            const filtered = userListTemp.filter(item =>
                item.groupNumber.toLowerCase().includes(query.toLowerCase())
            );
            setFilteredList(filtered);
        } else {
            setFilteredList([]);
        }
    }, [userListTemp]);

    const handleSelectItem = useCallback((groupNumber) => {
        if (!selectedGroups.includes(groupNumber)) {
            setSelectedGroups(prev => [...prev, groupNumber]);
        }
        setSearchQuery('');
        setFilteredList([]);
    }, [selectedGroups]);

    const handleSelectChange = useCallback((value) => {
        if (value && value !== 'Select Group Number' && !selectedGroups.includes(value)) {
            setSelectedGroups(prev => [...prev, value]);
        }
    }, [selectedGroups]);

    const handleRemoveGroup = useCallback((group) => {
        setSelectedGroups(prev => prev.filter(item => item !== group));
    }, []);

    const handleSelectChain = useCallback((index, chain) => {
        setUserChainNamesList(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                chainName: chain.chainName,
                comm: chain.commission || chain.comm || updated[index].comm
            };
            return updated;
        });
        setOpenPopovers(prev => ({ ...prev, [index]: false }));
    }, []);

    const handleChainNameChange = useCallback((index, value) => {
        setUserChainNamesList(prev => {
            const updated = [...prev];
            updated[index].chainName = value;
            return updated;
        });
        // Open popover when typing
        if (value) {
            setOpenPopovers(prev => ({ ...prev, [index]: true }));
        }
    }, []);

    const addNewChainRow = useCallback(() => {
        const newRow = {
            chainName: '',
            comm: '',
            editable: true
        };
        setUserChainNamesList(prev => [...prev, newRow]);
    }, []);

    const handleEdit = useCallback((index) => {
        setUserChainNamesList(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], editable: true };
            return updated;
        });
    }, []);

    const handleCancel = useCallback((index) => {
        setUserChainNamesList(prev => {
            const updated = [...prev];
            const row = updated[index];
            
            if (!row.chainName && !row.comm) {
                updated.splice(index, 1);
            } else {
                updated[index] = { ...updated[index], editable: false };
            }
            return updated;
        });
        setError('');
    }, []);

    const saveData = useCallback((index, name, commission) => {
        if (!name || !commission || !userService.validateCurrency(commission)) {
            setError("Fields can't be blank and commission should be a numeric value e.g. 0.10");
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (parseFloat(commission) > 10) {
            setError('Commission should not be more than 10');
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (parseFloat(commission) <= 0) {
            setError('Commission should be more than 0');
            setTimeout(() => setError(''), 3000);
            return;
        }

        setUserChainNamesList(prev => {
            const updated = [...prev];
            updated[index] = { chainName: name, comm: commission, editable: false };
            return updated;
        });
        setError('');
    }, []);

    const cancelList = useCallback((index) => {
        setUserChainNamesList(prev => {
            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        });
    }, []);

    const saveChainList = useCallback(async (chainData) => {
        try {
            const flag = noInitialChainList ? 0 : 1;
            const requestOptions = {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'PharmacyChainJson': JSON.stringify(chainData),
                    'X-Browser-Url': window.location.href
                },
                body: JSON.stringify({
                    Wholesaler_Name: whName,
                    Wholesaler_Id: whId,
                    flag: flag,
                    GroupNumber: selectedGroups.join(','),
                    GroupSpecific: true
                })
            };

            const response = await fetch(`/api/admin/PharmacyChainSetting_gs`, requestOptions);
            const apidata = await handleResponse(response);

            if (apidata?.length > 0) {
                if (apidata[0].status_code === "1") {
                    setSuccessMsg(apidata[0].status_message);
                    fetchUserPharmacyChainList(whName, whId);
                    setTimeout(() => {
                        setSuccessMsg('');
                    }, 3000);
                } else {
                    setError(apidata[0].status_message || 'Error saving chain list');
                    setTimeout(() => {
                        setError('');
                    }, 3000);
                }
            } else {
                setError('Error saving chain list');
                setTimeout(() => {
                    setError('');
                }, 3000);
            }
        } catch (error) {
            setError('Error saving chain list. Please try again.');
            setTimeout(() => {
                setError('');
            }, 3000);
        } finally {
            setBtnLoader(false);
            setLoading(false);
        }
    }, [whId, whName, selectedGroups, noInitialChainList, handleResponse, fetchUserPharmacyChainList]);

    const handleSaveSubmit = useCallback((e) => {
        e.preventDefault();
        setBtnLoader(true);
        setLoading(true);
        setError('');

        const isEditMode = userChainNamesList.some(row => row.editable === true);
        if (isEditMode) {
            setError('Please save all the fields first');
            setBtnLoader(false);
            setLoading(false);
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (selectedGroups.length === 0) {
            setError('Please select group number(s)');
            setBtnLoader(false);
            setLoading(false);
            setTimeout(() => setError(''), 3000);
            return;
        }

        const chainData = userChainNamesList.map(item => ({
            ChainName: item.chainName,
            Comm: item.comm
        }));

        if (chainData.length === 0) {
            setError('Please add pharmacy chain list');
            setBtnLoader(false);
            setLoading(false);
            setTimeout(() => setError(''), 3000);
            return;
        }

        saveChainList(chainData);
    }, [userChainNamesList, selectedGroups, saveChainList]);

    const handleBack = useCallback(() => {
        closemanageGroupSection(whName, whId, whPrefix, whProfilePagin, whSearch);
        openAddOverridePopup(whName, whId, whPrefix, whProfilePagin, whSearch, whEmail);
    }, [closemanageGroupSection, openAddOverridePopup, whName, whId, whPrefix, whProfilePagin, whSearch, whEmail]);

    const handleClose = useCallback(() => {
        closemanageGroupSection(whName, whId, whPrefix, whProfilePagin, whSearch);
        openAddOverridePopup(whName, whId, whPrefix, whProfilePagin, whSearch, whEmail);
    }, [closemanageGroupSection, openAddOverridePopup, whName, whId, whPrefix, whProfilePagin, whSearch, whEmail]);

    return (
        <Dialog open={true} onOpenChange={handleBack}>
            <DialogContent className="max-w-5xl">
                <DialogHeader>
                    <DialogTitle>{text || 'Manage Group & Chain List'}</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center items-center py-4">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : <>
                <form id="wh-manage-group-form" onSubmit={handleSaveSubmit} className="space-y-6">
                    <div className="space-y-4 border rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Search Group Numbers</Label>
                                <div className="relative">
                                    <Input
                                        type="text"
                                        placeholder="Search"
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        disabled={loading || btnLoader}
                                    />
                                    {filteredList.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-[200px] overflow-auto">
                                            {filteredList.map((item, index) => (
                                                <div
                                                    key={index}
                                                    className="px-3 py-2 cursor-pointer hover:bg-accent"
                                                    onClick={() => handleSelectItem(item.groupNumber)}
                                                >
                                                    {item.groupNumber}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Select Group Numbers</Label>
                                <Select onValueChange={handleSelectChange} disabled={loading || btnLoader}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select Group Number" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px] overflow-y-auto">
                                        {userListTemp.map((item, index) => (
                                            <SelectItem key={index} value={item.groupNumber}>
                                                {item.groupNumber}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {selectedGroups.length > 0 && (
                            <div className="space-y-2">
                                <Label>Selected Group Numbers:</Label>
                                <div className="flex flex-wrap gap-2">
                                    {selectedGroups.map((group, index) => (
                                        <Badge key={index} variant="secondary" className="gap-1">
                                            {group}
                                            <X
                                                className="h-3 w-3 cursor-pointer"
                                                onClick={() => handleRemoveGroup(group)}
                                            />
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-semibold">Chain List</h2>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={addNewChainRow}
                                disabled={loading || btnLoader}
                            >
                                Add New Chain
                            </Button>
                        </div>

                        {userChainNamesList.length > 0 ? (
                            <div className="border rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Commission</TableHead>
                                            <TableHead className="w-[150px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {userChainNamesList.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    {item.editable ? (
                                                        <Popover open={openPopovers[index]} onOpenChange={(open) => setOpenPopovers(prev => ({ ...prev, [index]: open }))}>
                                                            <PopoverTrigger asChild>
                                                        <Input
                                                            type="text"
                                                                    value={item.chainName}
                                                                    onChange={(e) => handleChainNameChange(index, e.target.value)}
                                                                    onFocus={() => setOpenPopovers(prev => ({ ...prev, [index]: true }))}
                                                                    placeholder="Search chain..."
                                                                    className="w-full"
                                                                />
                                                            </PopoverTrigger>
                                                            <PopoverContent 
                                                                className="w-[300px] p-0" 
                                                                align="start"
                                                                onOpenAutoFocus={(e) => e.preventDefault()}
                                                            >
                                                                <div className="max-h-[200px] overflow-y-auto">
                                                                    {chainNamesList
                                                                        .filter(chain => 
                                                                            !item.chainName || 
                                                                            chain.chainName.toLowerCase().includes(item.chainName.toLowerCase())
                                                                        )
                                                                        .length > 0 ? (
                                                                        chainNamesList
                                                                            .filter(chain => 
                                                                                !item.chainName || 
                                                                                chain.chainName.toLowerCase().includes(item.chainName.toLowerCase())
                                                                            )
                                                                            .map((chain, idx) => (
                                                                                <div
                                                                                    key={idx}
                                                                                    className="px-3 py-2 cursor-pointer hover:bg-accent text-sm border-b last:border-b-0"
                                                                                    onMouseDown={(e) => {
                                                                                        e.preventDefault();
                                                                                        handleSelectChain(index, chain);
                                                                                    }}
                                                                                >
                                                                                    {chain.chainName}
                                                                                </div>
                                                                            ))
                                                                    ) : (
                                                                        <div className="px-3 py-2 text-sm text-muted-foreground">
                                                                            No chain found.
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </PopoverContent>
                                                        </Popover>
                                                    ) : (
                                                        item.chainName
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {item.editable ? (
                                                        <Input
                                                            type="text"
                                                            value={item.comm}
                                                            onChange={(e) => {
                                                                const updated = [...userChainNamesList];
                                                                updated[index].comm = e.target.value;
                                                                setUserChainNamesList(updated);
                                                            }}
                                                        />
                                                    ) : (
                                                        item.comm
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {item.editable ? (
                                                        <div className="flex gap-2">
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                onClick={() => saveData(index, item.chainName, item.comm)}
                                                            >
                                                                Save
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="secondary"
                                                                onClick={() => handleCancel(index)}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex gap-2">
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleEdit(index)}
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => cancelList(index)}
                                                            >
                                                                <Trash2 className="h-4 w-4 text-destructive" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                No chain list added yet. Click "Add New Chain" to get started.
                            </div>
                        )}
                    </div>

                    {error && (
                        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                            {error}
                        </div>
                    )}

                    {successMsg && (
                        <div className="text-sm text-green-600 bg-green-50 p-3 rounded-md">
                            {successMsg}
                        </div>
                    )}

                </form>
                    <DialogFooter>
                        <Button type="submit" form="wh-manage-group-form" disabled={loading || btnLoader}>
                            {btnLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save & Submit
                        </Button>
                        <Button type="button" variant="secondary" onClick={handleBack} disabled={btnLoader}>
                            Cancel
                        </Button>
                    </DialogFooter>
                </>}
            </DialogContent>
        </Dialog>
    );
}

export default WhManageGroup;
